import { Box, useMediaQuery } from '@chakra-ui/react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronDownIcon } from '@/assets/icons';
import FaceliftMenu from '@/components/Atoms/FaceliftMenu';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import { useHoverMenu } from '@/hooks/useHoverMenu';
import usePermission from '@/hooks/usePermission';
import { useQuickActionsStore } from '@/store/quickActionsStore';
import { navigationItems } from '@/utils/navigationItems';
import {
	findNavItemByHrefWithParent,
	navItemToDisplayData,
} from '@/utils/quickActionsUtils';

interface QuickActionsDropdownProps {
	isCollapsed?: boolean;
}

const QuickActionsDropdown: React.FC<QuickActionsDropdownProps> = ({
	isCollapsed = false,
}) => {
	const navigate = useNavigate();
	const { hasPermission } = usePermission();
	const { quickActions, toggleQuickAction, isStarred } = useQuickActionsStore();
	const [openMenu, setOpenMenu] = useState(false);
	const { cancelClose, scheduleClose } = useHoverMenu(250);

	const [modalState, setModalState] = useState<{
		isOpen: boolean;
		isLoading: boolean;
		isSuccess: boolean;
		error: string | null;
	}>({
		isOpen: false,
		isLoading: false,
		isSuccess: false,
		error: null,
	});

	// Theme-aware colors
	const textColor = useColorModeValue('white', 'gray.100');
	const [isMidViewport] = useMediaQuery([
		'(min-width: 1030px) and (max-width: 1200px)',
	]);

	// Map hrefs to NavItems and filter by permissions
	const filteredQuickActions = useMemo(() => {
		return Object.entries(quickActions)
			.map(([href, storedPermission]) => {
				// Find nav item with parent permission context
				const { item: navItem, permission: itemPermission } =
					findNavItemByHrefWithParent(navigationItems, href);
				if (!navItem) return null;

				// Use stored permission from quickActions (which includes parent permissions)
				// or fall back to item's own permission
				const permissionToCheck = storedPermission || itemPermission;

				// Check if user has permission to see this item
				// If item has its own permissions, check those; otherwise check the stored permission
				if (navItem.permissions && navItem.permissions.length > 0) {
					if (!hasPermission(navItem.permissions)) {
						return null;
					}
				} else if (permissionToCheck) {
					// For items without own permissions, check the parent permission
					if (!hasPermission([permissionToCheck])) {
						return null;
					}
				}

				const displayData = navItemToDisplayData(navItem);
				return {
					...displayData,
					id: href, // Use href as id (unique identifier)
					storedPermission, // Store the permission for removal
				};
			})
			.filter((action): action is NonNullable<typeof action> => action !== null);
	}, [quickActions, hasPermission]);

	const handleNavigate = (href?: string) => {
		if (href) {
			navigate(href);
			setOpenMenu(false);
		}
	};

	// Handle action clicks - for now, all actions navigate
	// Modals can be added later by extending the actionType

	// Don't render the dropdown if no quick actions, but still render the modal
	if (filteredQuickActions.length === 0) {
		return (
			<>
				{/* Quick Action Toggle Modal */}
				<FaceliftSuccessProgressModal
					open={modalState.isOpen}
					isLoading={modalState.isLoading}
					isSucceed={modalState.isSuccess}
					hasError={!!modalState.error}
					onOpenChange={(open: boolean) => {
						if (!open) {
							setModalState({ isOpen: false, isLoading: false, isSuccess: false, error: null });
						}
					}}
					title="Quick Action Updated!"
					errorMessage={modalState.error || ''}
					timeoutTime={300}
				/>
			</>
		);
	}

	return (
		<Box
			display="inline-block"
			position="relative"
			onMouseEnter={cancelClose}
			onMouseLeave={() => {
				scheduleClose(() => setOpenMenu(false));
			}}
		>
			<FaceliftMenu
				showStars={true}
				isStarred={(id) => {
					// id is the href string
					return id ? isStarred(id) : false;
				}}
				onStarClick={async (menuItem, e) => {
					e.preventDefault();
					e.stopPropagation();
					
					// Close the menu first
					setOpenMenu(false);
					
					// Remove from quick actions by toggling
					if (menuItem.href) {
						const action = filteredQuickActions.find(
							(a) => a.href === menuItem.href,
						);
						const permission = action?.storedPermission || null;
						if (permission) {
							setModalState({ isOpen: true, isLoading: true, isSuccess: false, error: null });

							try {
								await toggleQuickAction(menuItem.href, permission);
								setModalState({ isOpen: true, isLoading: false, isSuccess: true, error: null });
							} catch (error) {
								setModalState({
									isOpen: true,
									isLoading: false,
									isSuccess: false,
									error: error instanceof Error ? error.message : 'Failed to update quick actions',
								});
							}
						}
					}
				}}
				trigger={
					<Box
						as="div"
						display="inline-flex"
						alignItems="center"
						px={isCollapsed ? '2' : '3'}
						py="2"
						borderRadius="md"
						color={textColor}
						fontWeight={600}
						fontSize="sm"
						cursor="pointer"
						transition="all 0.3s ease-in-out"
						minW={isCollapsed ? '10' : 'auto'}
						bg="transparent"
						_hover={{ bg: 'transparent' }}
						_active={{ bg: 'transparent' }}
						onMouseEnter={() => {
							cancelClose();
							setOpenMenu(true);
						}}
						onMouseLeave={() => {
							scheduleClose(() => setOpenMenu(false));
						}}
					>
						<Box
							as="span"
							whiteSpace={isMidViewport && !isCollapsed ? 'normal' : 'nowrap'}
							textAlign={isMidViewport && !isCollapsed ? 'center' : 'left'}
						>
							Quick Actions
						</Box>
						<Show when={!isCollapsed}>
							<Box
								ml="2"
								display="inline-flex"
								alignItems="center"
								justifyContent="center"
								transition="transform 0.2s ease"
								transform={openMenu ? 'rotate(180deg)' : undefined}
								mt="0.5"
							>
								<ChevronDownIcon width={10} height={5} color={textColor} />
							</Box>
						</Show>
					</Box>
				}
				items={filteredQuickActions.map((action) => ({
					title: action.title,
					href: action.href,
					permissions: action.permissions,
					id: action.id, // This is the href string (unique identifier)
				}))}
				open={openMenu}
				onOpenChange={(open) => {
					setOpenMenu(open);
				}}
				onMouseEnter={cancelClose}
				onMouseLeave={() => {
					scheduleClose(() => setOpenMenu(false));
				}}
				onNavigate={handleNavigate}
			/>

			{/* Quick Action Toggle Modal */}
			<FaceliftSuccessProgressModal
				open={modalState.isOpen}
				isLoading={modalState.isLoading}
				isSucceed={modalState.isSuccess}
				hasError={!!modalState.error}
				onOpenChange={(open: boolean) => {
					if (!open) {
						setModalState({ isOpen: false, isLoading: false, isSuccess: false, error: null });
					}
				}}
				title="Quick Action Updated!"
				errorMessage={modalState.error || ''}
				timeoutTime={300}
			/>
		</Box>
	);
};

export default QuickActionsDropdown;
